// Conferências da escolha de conversões — o que costuma inflar o número sem ninguém notar.
// Sem dependências: testado com `npm test` (node --test).

/** O que as conferências precisam de uma ação de conversão. */
export type AcaoBasica = { id: string; nome: string; idExterno: string; categoria: string | null; contaId: string; plataforma: string }

/**
 * Evento que uma ação do Meta mede, sem o prefixo da origem. Duas ações com o mesmo
 * evento na mesma conta (ex.: omni_purchase e offsite_conversion.fb_pixel_purchase)
 * contam a mesma compra duas vezes.
 */
export function eventoBaseMeta(idExterno: string): string {
  return idExterno
    .replace(/^omni_/, '')
    .replace(/^offsite_conversion\.fb_pixel_/, '')
    .replace(/^onsite_web_app_/, '')
    .replace(/^onsite_web_/, '')
    .replace(/^onsite_conversion\./, '')
}

// No Meta, algumas ações são a soma de outras: marcar a soma e uma parte conta a parte duas vezes.
const PARTES_DO_ENGAJAMENTO = ['post_reaction', 'comment', 'link_click', 'video_view', 'post_save', 'post', 'photo_view', 'like']
const CONTEM: Record<string, string[]> = {
  page_engagement: ['post_engagement', ...PARTES_DO_ENGAJAMENTO],
  post_engagement: PARTES_DO_ENGAJAMENTO,
}

/** Grupos de ações escolhidas que provavelmente contam a mesma coisa duas vezes. */
export function sobreposicoes<T extends AcaoBasica>(escolhidas: T[]): T[][] {
  const meta = escolhidas.filter((a) => a.plataforma === 'meta_ads')
  const grupos = new Map<string, T[]>()
  for (const a of meta) {
    const chave = `${a.contaId}:${eventoBaseMeta(a.idExterno)}`
    grupos.set(chave, [...(grupos.get(chave) ?? []), a])
  }
  const resultado = [...grupos.values()].filter((g) => g.length > 1)
  // Soma + parte, na mesma conta.
  for (const total of meta) {
    const partes = CONTEM[eventoBaseMeta(total.idExterno)]
    if (!partes) continue
    const dentro = meta.filter((a) => a !== total && a.contaId === total.contaId && partes.includes(eventoBaseMeta(a.idExterno)))
    if (dentro.length) resultado.push([total, ...dentro])
  }
  return resultado
}

// Eventos do GA4 que medem visita ou atenção, não resultado.
const ENGAJAMENTO_GA4 = new Set(['page_view', 'scroll', 'session_start', 'first_visit', 'user_engagement', 'click', 'view_search_results', 'file_download', 'video_start', 'video_progress', 'video_complete'])

/** Ação que mede atenção (curtida, visualização, clique), não resultado. */
export function ehEngajamento(a: AcaoBasica): boolean {
  if (a.plataforma === 'ga4') return ENGAJAMENTO_GA4.has(a.idExterno)
  return a.categoria === 'ENGAGEMENT'
}

/** Cliques e visitas à página de destino: engajamento, mas o resultado certo para marca com objetivo de tráfego. */
export function ehTrafego(a: AcaoBasica): boolean {
  return a.plataforma === 'meta_ads' && ['link_click', 'landing_page_view'].includes(eventoBaseMeta(a.idExterno))
}

export type Conferencia = {
  /** Ações de engajamento escolhidas (fora cliques e visitas à página, que valem para tráfego). */
  engajamento: AcaoBasica[]
  /** Há clique no link ou visita à página escolhidos (válido só se o objetivo for tráfego). */
  trafego: AcaoBasica[]
  /** Conversões de anúncio > cliques nos anúncios no mesmo período: quase sempre escolha errada. */
  maisQueCliques: boolean
}

export function conferirEscolha(escolhidas: AcaoBasica[], conversoesAnuncio: number, cliques: number): Conferencia {
  return {
    engajamento: escolhidas.filter((a) => ehEngajamento(a) && !ehTrafego(a)),
    trafego: escolhidas.filter(ehTrafego),
    maisQueCliques: cliques > 0 && conversoesAnuncio > cliques,
  }
}
